import React, { Fragment, useState } from 'react';
import styled from 'styled-components';

import { Box } from '../Box';
import { Icons } from '../Icons';

type Item<T> = {
  key: string;
  childCount?: number;
  isExpanded?: boolean;
  children?: T[];
};

type Props<T extends Item<T>> = {
  data: T[];
  renderNode: (item: T) => React.ReactNode;
  onExpand?: (item: T) => void;
};

const ChevronIcon = styled(Box)<{ isExpanded: boolean }>`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ChevronSpacer = styled(Box)`
  width: 24px;
  flex-shrink: 0;
`;

const collectExpandedKeys = <T extends Item<T>>(data: T[]): Set<string> => {
  const keys = new Set<string>();
  data.forEach(item => {
    if (item.isExpanded) keys.add(item.key);
  });
  return keys;
};

export const Tree = <T extends Item<T>>({
  data,
  renderNode,
  onExpand,
}: Props<T>) => {
  const [expandedItemsKey, setExpandedItemsKey] = useState<Set<string>>(() =>
    collectExpandedKeys(data)
  );

  const handleToggle = (item: T) => {
    const isExpanding = !expandedItemsKey.has(item.key);

    setExpandedItemsKey(prev => {
      const next = new Set(prev);
      if (next.has(item.key)) {
        next.delete(item.key);
      } else {
        next.add(item.key);
      }
      return next;
    });

    if (isExpanding && onExpand) {
      onExpand(item);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      flexGap="4px"
      overflow="hidden"
    >
      {data.map(item => {
        const hasChildren =
          (item.children && item.children.length > 0) ||
          (item.childCount ?? 0) > 0;
        const hasRenderedChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItemsKey.has(item.key);

        return (
          <Fragment key={item.key}>
            <Box display="flex" alignItems="center" flexGap="4px" width="100%">
              {hasChildren ? (
                <ChevronIcon
                  isExpanded={isExpanded}
                  onClick={() => handleToggle(item)}
                >
                  {isExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                </ChevronIcon>
              ) : (
                <ChevronSpacer />
              )}

              {renderNode(item)}
            </Box>

            {hasRenderedChildren && isExpanded && (
              <Box mt={1} ml={4} width="-webkit-fill-available">
                <Tree
                  data={item.children!}
                  renderNode={renderNode}
                  onExpand={onExpand}
                />
              </Box>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};
